import { useEffect, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { getDelayValueFromRange } from './delay-utils';
import { PulsatingCircle } from './loading';
import {
  StreamStatus,
  useContentStream,
  type UseContentStreamProps,
} from './use-content-stream';

enum ContentStreamDisplayStatus {
  Loaded = 'loaded',
  Loading = 'loading',
}

export interface ContentStreamProps extends UseContentStreamProps {
  bypassStream?: boolean;
  children: ({
    displayStatus,
    rawContent,
    markdownContent,
    streamStatus,
  }: {
    displayStatus: ContentStreamDisplayStatus;
    rawContent: string;
    markdownContent: string;
    streamStatus: StreamStatus;
  }) => ReactNode;
  loadingComponent?: () => ReactNode;
  onEnd?: (content: string) => void;
  onStream?: ({
    lastToken,
    markdownBuffer,
    rawBuffer,
  }: {
    lastToken: string;
    markdownBuffer: string;
    rawBuffer: string;
  }) => void;
  startDelayRange?: [number] | [number, number];
}

export const ContentStream = ({
  bypassStream = false,
  children,
  content: originalContent,
  tokenDelayRange = [0, 0],
  loadingComponent: LoadingComponent = PulsatingCircle,
  splitBy,
  startDelayRange = [0],
  onEnd,
  onStream,
}: ContentStreamProps) => {
  const delay = getDelayValueFromRange(startDelayRange);

  const initialStatus = (() => {
    if (delay > 0 && !bypassStream) {
      return ContentStreamDisplayStatus.Loading;
    }
    return ContentStreamDisplayStatus.Loaded;
  })();

  const [displayStatus, setDisplayStatus] = useState(initialStatus);

  const {
    lastToken,
    markdownStream: markdownStreamContent,
    rawStream: rawStreamContent,
    status: streamStatus,
  } = useContentStream({
    content: originalContent,
    tokenDelayRange,
    enabled:
      !bypassStream && displayStatus === ContentStreamDisplayStatus.Loaded,
    splitBy,
  });

  useEffect(() => {
    if (!!onEnd && streamStatus === StreamStatus.Ended) {
      onEnd(originalContent);
    }
  }, [onEnd, originalContent, streamStatus]);

  useEffect(() => {
    if (!!onStream && streamStatus === StreamStatus.Streaming) {
      onStream({
        lastToken,
        rawBuffer: rawStreamContent,
        markdownBuffer: markdownStreamContent,
      });
    }
  }, [
    onStream,
    markdownStreamContent,
    rawStreamContent,
    streamStatus,
    lastToken,
  ]);

  useEffect(() => {
    if (displayStatus === ContentStreamDisplayStatus.Loading) {
      const timer = setTimeout(
        () => setDisplayStatus(ContentStreamDisplayStatus.Loaded),
        delay
      );
      return () => clearTimeout(timer);
    }
  }, [displayStatus, delay]);

  const renderContent = () => {
    if (displayStatus === ContentStreamDisplayStatus.Loading) {
      return (
        <Box component="span" data-testid="loading">
          <LoadingComponent />
        </Box>
      );
    }

    return children({
      displayStatus,
      markdownContent: bypassStream ? originalContent : markdownStreamContent,
      rawContent: bypassStream ? originalContent : rawStreamContent,
      streamStatus,
    });
  };

  return (
    <Box
      sx={{
        '& .streaming': {
          '&-token': {
            filter: 'brightness(1.5)',
            maskImage:
              'linear-gradient(to right, black 0%, black 40%, transparent 90%)',
            maskRepeat: 'no-repeat',
          },

          '&-caret': {
            alignItems: 'center',
            backgroundColor: 'black',
            borderRadius: '50%',
            content: '""',
            display: 'inline-flex',
            height: '6px',
            justifyContent: 'center',
            marginLeft: '6px',
            width: '6px',
          },
        },
      }}
    >
      {renderContent()}
    </Box>
  );
};
